import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import uptimeMonitorService, { UptimeMonitor, CreateMonitorData } from '../../services/api/uptime-monitor.service';

interface MonitorFormModalProps {
  monitor: UptimeMonitor | null;
  onClose: () => void;
}

export default function MonitorFormModal({ monitor, onClose }: MonitorFormModalProps) {
  const { t } = useTranslation('uptime');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateMonitorData>({
    name: '',
    description: '',
    type: 'https',
    url: '',
    method: 'GET',
    interval_seconds: 60,
    timeout_seconds: 30,
    expected_status_code: 200,
    expected_response_contains: '',
    retry_count: 3,
    failure_threshold: 3,
    success_threshold: 2,
    alert_on_failure: true,
    alert_on_recovery: true,
    ssl_check: false,
    ssl_expiry_days_warning: 30,
  });

  useEffect(() => {
    if (monitor) {
      setFormData({
        name: monitor.name,
        description: monitor.description || '',
        type: monitor.type,
        url: monitor.url,
        method: monitor.method,
        headers: monitor.headers,
        body: monitor.body,
        interval_seconds: monitor.interval_seconds,
        timeout_seconds: monitor.timeout_seconds,
        expected_status_code: monitor.expected_status_code,
        expected_response_contains: monitor.expected_response_contains || '',
        retry_count: monitor.retry_count,
        failure_threshold: monitor.failure_threshold,
        success_threshold: monitor.success_threshold,
        alert_on_failure: monitor.alert_on_failure,
        alert_on_recovery: monitor.alert_on_recovery,
        status_page_component_id: monitor.status_page_component_id,
        notification_channels: monitor.notification_channels,
        tags: monitor.tags,
        ssl_check: monitor.ssl_check,
        ssl_expiry_days_warning: monitor.ssl_expiry_days_warning,
      });
    }
  }, [monitor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (monitor) {
        await uptimeMonitorService.update(monitor.id, formData);
        toast.success(t('messages.monitorUpdated'));
      } else {
        await uptimeMonitorService.create(formData);
        toast.success(t('messages.monitorCreated'));
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving monitor:', error);
      toast.error(error.response?.data?.error || t('messages.errorSaving'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateMonitorData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="relative inline-block w-full max-w-2xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:p-6">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            {monitor ? t('editMonitor') : t('createMonitor')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                  placeholder={t('form.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows={2}
                  placeholder={t('form.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.type')}
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="https">HTTPS</option>
                    <option value="http">HTTP</option>
                    <option value="tcp">TCP</option>
                    <option value="ping">Ping</option>
                    <option value="dns">DNS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.method')}
                  </label>
                  <select
                    value={formData.method}
                    onChange={(e) => handleChange('method', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    disabled={formData.type !== 'http' && formData.type !== 'https'}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="HEAD">HEAD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.url')} *
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => handleChange('url', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                  placeholder={t('form.urlPlaceholder')}
                />
              </div>
            </div>

            {/* Timing Settings */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('form.timingSettings')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.interval')}
                  </label>
                  <select
                    value={formData.interval_seconds}
                    onChange={(e) => handleChange('interval_seconds', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value={30}>30 {t('form.seconds')}</option>
                    <option value={60}>1 {t('form.minute')}</option>
                    <option value={300}>5 {t('form.minutes')}</option>
                    <option value={600}>10 {t('form.minutes')}</option>
                    <option value={900}>15 {t('form.minutes')}</option>
                    <option value={1800}>30 {t('form.minutes')}</option>
                    <option value={3600}>1 {t('form.hour')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.timeout')}
                  </label>
                  <select
                    value={formData.timeout_seconds}
                    onChange={(e) => handleChange('timeout_seconds', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value={5}>5 {t('form.seconds')}</option>
                    <option value={10}>10 {t('form.seconds')}</option>
                    <option value={15}>15 {t('form.seconds')}</option>
                    <option value={30}>30 {t('form.seconds')}</option>
                    <option value={60}>60 {t('form.seconds')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Validation Settings */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('form.validationSettings')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.expectedStatusCode')}
                  </label>
                  <input
                    type="number"
                    value={formData.expected_status_code}
                    onChange={(e) => handleChange('expected_status_code', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    min={100}
                    max={599}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('form.expectedStatusCodeHint', { defaultValue: 'Tip: 200 accepts any 2xx status code (200-299). For exact match, use another value.' })}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('form.retryCount')}
                  </label>
                  <input
                    type="number"
                    value={formData.retry_count}
                    onChange={(e) => handleChange('retry_count', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    min={0}
                    max={10}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('form.expectedResponseContains')}
                </label>
                <input
                  type="text"
                  value={formData.expected_response_contains}
                  onChange={(e) => handleChange('expected_response_contains', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder={t('form.expectedResponseContainsPlaceholder')}
                />
              </div>
            </div>

            {/* Alert Settings */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('form.alertSettings')}</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.alert_on_failure}
                    onChange={(e) => handleChange('alert_on_failure', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('form.alertOnFailure')}</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.alert_on_recovery}
                    onChange={(e) => handleChange('alert_on_recovery', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('form.alertOnRecovery')}</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.ssl_check}
                    onChange={(e) => handleChange('ssl_check', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('form.sslCheck')}</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? t('common:buttons.saving') : monitor ? t('common:buttons.save') : t('common:buttons.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
